import { useCallback, useState, type FC } from "react";

import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { EditSecretForm, type EditSecretFormOnSubmitInput } from "@/components/secrets/EditSecretForm";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateSecretValue, type PlainSecret } from "@/queries/secret-queries";

export const EditSecretModal: FC<{ secret: PlainSecret; onClose: () => void }> = ({ secret, onClose }) => {
    const { toast } = useToast();
    const updateSecretValue = useUpdateSecretValue();

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const onSubmit = useCallback(
        async ({ secretValuePlaintext }: EditSecretFormOnSubmitInput) => {
            try {
                await updateSecretValue.mutateAsync(
                    {
                        secretId: secret.id,
                        plaintextValue: secretValuePlaintext,
                    },
                    {
                        onSuccess: () => {
                            toast({
                                title: `${secret.name} was successfully updated`,
                            });
                        },
                    },
                );
                onClose();
            } catch {
                toast({
                    title: "Failed to update secret",
                    description: "An error occurred while updating the secret",
                });
            }
        },
        [secret.id, secret.name, updateSecretValue, onClose, toast],
    );

    const [disabled, setDisabled] = useState<boolean>(true);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="edit-secret-form-modal-content"
                data-track-location={TrackLocations.EditSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>Edit secret</DialogTitle>
                    <DialogDescription>
                        Update the secret value. File path and secret type cannot be changed.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={updateSecretValue.error}
                        data-testid="error-message-secret-update-failed"
                        className="text-center"
                    />
                    <EditSecretForm
                        formName="edit-secret-form"
                        onSubmit={onSubmit}
                        onDisabledChange={setDisabled}
                        secret={secret}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="edit-secret-form"
                        loading={updateSecretValue.isPending}
                        disabled={updateSecretValue.isPending || disabled}
                        data-testid="edit-secret-submit-button"
                        data-tracking-id="confirm-update-secret-edit-secret-modal"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
